import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

/**
 * Онбординг франчайзи и студий (миграция 20260803120000).
 * Перенос раннбуков supabase/operations:
 *   * partners/onboarding/onboard_new_studio — новый франчайзи + студия-заготовка;
 *   * partners/onboarding/onboard_buying_no_contract — новый франчайзи + BUYING;
 *   * studios/planning_and_opening/create_planned_studio — ещё одна студия партнёру;
 *   * studios/planning_and_opening/register_preopen_studio — номер и адрес заготовки.
 * Договор ни один из путей не создаёт: ДКК заводится в реестре документов.
 */

export type OnboardingPersonInput = {
  first_name: string
  last_name: string
  gender?: 'м' | 'ж' | null
}

export type OnboardingCity = {
  city_id: number
  city_name: string
  country: string
  studio_count: number
}

export type PreopenStudio = {
  studio_internal_id: number
  city_id: number | null
  city_name: string | null
  country: string | null
  status_code: string | null
  address: string | null
  studio_number: number | null
  series_id: number | null
  partner_number: number | null
  persons_fio: string
  ownership_phase: string | null
  created_at: string
}

export type OnboardPartnerNewStudioResult = {
  partner_number: number
  created_partner: boolean
  series_id: number
  city_id: number
  studio_internal_id: number
}

export type OnboardPartnerStudioPurchaseResult = {
  partner_number: number
  created_partner: boolean
  series_id: number
  studio_internal_id: number
  studio_number: number | null
  ownership_id: number
}

export type PlanNewStudioResult = {
  partner_number: number
  series_id: number
  city_id: number
  studio_internal_id: number
}

export type RegisterPreopenStudioResult = {
  studio_internal_id: number
  studio_number: number
  address: string
  partner_number: number | null
}

export type PopulationGroup = {
  population_threshold: number
  description: string
}

export type LandGroup = {
  land_group_code: number
  land_group_name: string
}

export async function fetchCityTimezones(): Promise<string[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any)
      .from('cities')
      .select('iana_timezone')
      .not('iana_timezone', 'is', null)
  )) as { data: Array<{ iana_timezone: string | null }> | null; error: any }

  if (error) throw error
  return Array.from(new Set((data ?? []).map(row => row.iana_timezone).filter((value): value is string => Boolean(value)))).sort()
}

export type OpenStudioResult = {
  studio_internal_id: number
  studio_number: string
  old_status: string | null
  new_status: 'OPEN'
  ownership_id: number
  old_phase: string | null
  new_phase: 'OWNED'
  partner_number: number
  changed_at: string
  status_changed: boolean
  phase_changed: boolean
  group_created: boolean
}

export async function fetchOnboardingCities(): Promise<OnboardingCity[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_onboarding_cities')
  )) as { data: OnboardingCity[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchPreopenStudios(): Promise<PreopenStudio[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_preopen_studios')
  )) as { data: PreopenStudio[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchPopulationGroups(): Promise<PopulationGroup[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any)
      .from('population_groups')
      .select('population_threshold, description')
      .order('population_threshold', { ascending: true })
  )) as { data: PopulationGroup[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchLandGroups(): Promise<LandGroup[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any)
      .from('land_groups')
      .select('land_group_code, land_group_name')
      .order('land_group_code', { ascending: true })
  )) as { data: LandGroup[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchRegisteredPreopenStudios(): Promise<PreopenStudio[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_registered_preopen_studios')
  )) as { data: PreopenStudio[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function onboardPartnerNewStudio(params: {
  persons: OnboardingPersonInput[]
  existingPartnerNumber?: number | null
  cityId?: number | null
  cityName?: string | null
  country?: string | null
  populationThousands?: number | null
  cityPopulationGroup?: number | null
  cityLandGroup?: number | null
  ianaTimezone?: string | null
  note?: string | null
}): Promise<OnboardPartnerNewStudioResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('onboard_partner_new_studio_with_city_metadata', {
      p_persons: params.persons,
      p_existing_partner_number: params.existingPartnerNumber ?? null,
      p_city_id: params.cityId ?? null,
      p_city_name: params.cityName?.trim() || null,
      p_country: params.country?.trim() || null,
      p_population_thousands: params.populationThousands ?? null,
      p_city_population_group: params.cityPopulationGroup ?? null,
      p_city_land_group: params.cityLandGroup ?? null,
      p_iana_timezone: params.ianaTimezone?.trim() || 'Europe/Moscow',
      p_note: params.note?.trim() || null,
    })
  )) as { data: OnboardPartnerNewStudioResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Франчайзи не заведён')
  return data
}

export async function onboardPartnerStudioPurchase(params: {
  persons: OnboardingPersonInput[]
  studioInternalId: number
  existingPartnerNumber?: number | null
  note?: string | null
}): Promise<OnboardPartnerStudioPurchaseResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('onboard_partner_studio_purchase', {
      p_persons: params.persons,
      p_studio_internal_id: params.studioInternalId,
      p_existing_partner_number: params.existingPartnerNumber ?? null,
      p_note: params.note?.trim() || null,
    })
  )) as { data: OnboardPartnerStudioPurchaseResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Франчайзи не заведён')
  return data
}

export async function planNewStudioForPartner(params: {
  partnerNumber: number
  cityId?: number | null
  cityName?: string | null
  country?: string | null
  populationThousands?: number | null
  cityPopulationGroup?: number | null
  cityLandGroup?: number | null
  ianaTimezone?: string | null
  newSeries?: boolean
  note?: string | null
}): Promise<PlanNewStudioResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('plan_new_studio_for_partner_with_city_metadata', {
      p_partner_number: params.partnerNumber,
      p_city_id: params.cityId ?? null,
      p_city_name: params.cityName?.trim() || null,
      p_country: params.country?.trim() || null,
      p_new_series: params.newSeries ?? false,
      p_population_thousands: params.populationThousands ?? null,
      p_city_population_group: params.cityPopulationGroup ?? null,
      p_city_land_group: params.cityLandGroup ?? null,
      p_iana_timezone: params.ianaTimezone?.trim() || 'Europe/Moscow',
      p_note: params.note?.trim() || null,
    })
  )) as { data: PlanNewStudioResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия не запланирована')
  return data
}

export async function registerPreopenStudio(params: {
  studioInternalId: number
  address: string
  studioNumber?: number | null
  note?: string | null
}): Promise<RegisterPreopenStudioResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('register_preopen_studio', {
      p_studio_internal_id: params.studioInternalId,
      p_address: params.address.trim(),
      p_studio_number: params.studioNumber ?? null,
      p_note: params.note?.trim() || null,
    })
  )) as { data: RegisterPreopenStudioResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Номер студии не присвоен')
  return data
}

export async function openStudioFromOriginalCase(params: {
  studioNumber: string
  changedAt: string
}): Promise<OpenStudioResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('open_studio_from_original_case', {
      v_studio_number: params.studioNumber,
      v_start_date: params.changedAt,
    })
  )) as { data: OpenStudioResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Студия не открыта')
  return data
}
